import React, { useEffect, useState } from "react";
import { FiBell, FiKey, FiFileText, FiList } from "react-icons/fi";
import { AiFillEye, AiFillEyeInvisible, AiOutlineSwap } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import { useForm } from "react-hook-form";
import { siteRoutes } from "utils/helpers/enums/routes";
import RsvpBreadcrumb, {
  RsvpBreadcrumbLink,
} from "components/breadcrumbs/RsvpBreadcrumb";
import useRsvp from "../useHooks";
import { warningToaster } from "utils/helpers/common/alert-service";
import useAuth from "containers/auth/useHooks";

type FormValues = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};
const RsvpGeneralSettings: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const [activeSection, setActiveSection] = useState("Privacy Policy");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [minorProfileData, setMinorProfileData] = useState<any>(null);
  const [enableNotifications, setEnableNotifications] = useState(
    Boolean(minorProfileData?.notificationEnabled)
  );
  const [termConditionContent, setTermConditionContent] = useState<[]>([]);
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<[]>([]);
  const { getPrivacyPolicy } = useAuth();

  const { getRsvpProfile, toggleNotification, changePassword } = useRsvp();

  const getData = async () => {
    await getRsvpProfile((profile: any) => {
      setMinorProfileData(profile);
      setEnableNotifications(profile?.notificationEnabled);
    });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleToggleNotifications = async () => {
    setEnableNotifications((prev) => !prev);
    await toggleNotification();
  };

  const breadcrumbLinks: RsvpBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.rsvpHome },
    { title: "General Settings", path: siteRoutes.rsvpGeneralSettings },
  ];

  const onSubmit = async (data: FormValues) => {
    if (data.newPassword !== data.confirmPassword) {
      warningToaster("Passwords do not match!");
    } else {
      await changePassword(data);
    }
  };

  const validatePassword = (password: string) => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("one lowercase letter");
    }
    if (!/\d/.test(password)) {
      errors.push("one number");
    }
    if (!/[\W_]/.test(password)) {
      errors.push("one special character");
    }
    // if (/\s/.test(password)) {
    //   errors.push("no spaces");
    // }

    return errors.length > 0
      ? `Password must include at least ${errors.join("/")}`
      : true;
  };

  const sidebarLinks = [
    { id: 1, label: "Notifications", icon: <FiBell /> },
    { id: 2, label: "Change password", icon: <FiKey /> },
    { id: 3, label: "Privacy Policy", icon: <FiFileText /> },
    { id: 4, label: "Term & Condition", icon: <FiList /> },
  ];

  useEffect(() => {
    getPrivacyPolicy(
      { heading: "Host User Agreement" },
      setTermConditionContent
    );
    getPrivacyPolicy({ heading: "Privacy Policy" }, setPrivacyPolicyContent);
  }, []);

  return (
    <div className="container">
      <RsvpBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-3xl text-blackie font-medium mb-4">
        General Settings
      </h2>

      <div className="flex flex-wrap sm:min-h-screen">
        {/* Sidebar */}

        <div className="w-full sm:w-[35%] md:w-1/4 bg-white">
          <ul>
            {sidebarLinks.map((link) => (
              <li
                key={link.id}
                onClick={() => {
                  if (link.label !== "Notifications") {
                    setActiveSection(link.label);
                  }
                }}
                className={`flex items-center justify-between px-3 py-4 cursor-pointer rounded-lg bordr-linear ${
                  activeSection === link.label && link.label !== "Notifications"
                    ? "text-rsvpGreen"
                    : ""
                }`}
              >
                {/* Left Section: Icon and Label */}
                <div className="flex items-center">
                  <span
                    className={`mr-3 text-xl ${
                      activeSection === link.label &&
                      link.label !== "Notifications"
                        ? "text-primaryBlueBg"
                        : "text-primaryBlueText"
                    }`}
                  >
                    {link.icon}
                  </span>
                  <span
                    className={`${
                      activeSection === link.label &&
                      link.label !== "Notifications"
                        ? "text-rsvpGreen"
                        : "text-ambassadorCard"
                    }`}
                  >
                    {link.label}
                  </span>
                </div>

                {/* Right Section: Arrow Icon or Notification Toggle */}
                {link.label === "Notifications" ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleNotifications();
                    }}
                    className={`w-10 h-6 flex items-center rounded-full p-1 ${
                      enableNotifications ? "bg-[#7A9526]" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`h-4 w-4 bg-white rounded-full transform duration-300 ${
                        enableNotifications ? "translate-x-4" : ""
                      }`}
                    ></div>
                  </button>
                ) : (
                  <MdArrowForwardIos
                    className={`text-lg ${
                      activeSection === link.label
                        ? "text-rsvpGreen"
                        : "text-gray-500"
                    }`}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Main Content */}
        <div className="flex-1 mt-5 sm:mt-0 sm:ml-6 ">
          {activeSection === "Change password" && (
            <div className="w-full md:w-2/3  flex flex-col">
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full">
                  <div className="w-full mb-6">
                    <h1 className="text-2xl text-blackie font-semibold">
                      Change Password
                    </h1>
                    <p className="text-sm text-labelTextColor mt-2">
                      Please enter old password and confirm to continue.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <label className="custom-label">Old Password</label>
                        <div
                          className="cursor-pointer flex items-center text-label text-sm"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>

                      <input
                        type={showOldPassword ? "text" : "password"}
                        placeholder="Enter your old password"
                        {...register("oldPassword", {
                          required: "Old Password is required",
                        })}
                        className="custom-input"
                      />

                      {errors.oldPassword && (
                        <span className="text-red-500 text-sm">
                          {errors.oldPassword.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1 mt-4">
                      <div className="flex items-center justify-between">
                        <label className="custom-label">New Password</label>
                        <div
                          className="cursor-pointer flex items-center text-label text-sm"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>

                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your new password"
                        {...register("newPassword", {
                          required: "New Password is required",
                          minLength: {
                            value: 8,
                            message:
                              "Password must be at least 8 characters long",
                          },
                          validate: {
                            noLeadingTrailingSpaces: (value) =>
                              !/\s/.test(value) ||
                              "Password does not contain spaces.",
                            validatePassword,
                          },
                        })}
                        className="custom-input"
                      />

                      {errors.newPassword && (
                        <span className="text-red-500 text-sm">
                          {errors.newPassword.message}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1 mt-4">
                      <div className="flex items-center justify-between">
                        <label className="custom-label">Confirm Password</label>
                        <div
                          className="cursor-pointer flex items-center text-label text-sm"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <>
                              <AiFillEyeInvisible size={18} className="mr-1" />
                              <span>Hide</span>
                            </>
                          ) : (
                            <>
                              <AiFillEye size={18} className="mr-1" />
                              <span>Show</span>
                            </>
                          )}
                        </div>
                      </div>

                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your new password"
                        {...register("confirmPassword", {
                          required: "Confirm Password is required",
                        })}
                        className="custom-input"
                      />

                      {errors.confirmPassword && (
                        <span className="text-red-500 text-sm">
                          {errors.confirmPassword.message}
                        </span>
                      )}
                    </div>

                    <div className="flex justify-start flex-col gap-2 pt-4">
                      <button
                        type="submit"
                        className="h-[64px]  text-xl bg-primaryBlueBg rounded-full text-white w-full sm:w-1/2"
                      >
                        Reset Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          {activeSection === "Privacy Policy" && (
            <div>
              {privacyPolicyContent?.map((item: any) => (
                <div key={item.id} className="space-y-2">
                  <h2 className="text-2xl font-bold text-black mb-4">
                    {item.heading}
                  </h2>
                  <div
                    className="html-content text-labelTextColor text-sm"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </div>
              ))}
            </div>
          )}
          {activeSection === "Term & Condition" && (
            <div>
              {termConditionContent?.map((item: any) => (
                <div key={item.id} className="space-y-2">
                  <h2 className="text-2xl font-bold text-black mb-4">
                    {item.heading}
                  </h2>
                  <div
                    className="html-content text-labelTextColor text-sm"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RsvpGeneralSettings;
